
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

const APPLICATION = 'HUB';
const DEFAULT_INDEX_PREFIX = 'logs';

enum LogLevel {
  Error = 'Error',
  Information = 'Information',
  Warning = 'Warning',
  Debug = 'Debug',
}

interface LogParams {
  service: string;
  message: string;
  details?: any;
}

@Injectable({ providedIn: 'root' })
export class ElasticLoggerService {
  private readonly environmentName = environment.name ?? 'LOCAL';
  private readonly appVersion = environment.appVersion;
  private readonly debugMode = environment.elastic?.debug ?? false;
  private readonly url = environment.elastic?.url ?? '';
  private readonly user = environment.elastic?.user ?? '';
  private readonly pass = environment.elastic?.pass ?? '';
  private readonly httpWithoutInterceptors: HttpClient;

  constructor(private readonly httpBackend: HttpBackend) {
    this.httpWithoutInterceptors = new HttpClient(httpBackend);
  }

  private get isConfigured(): boolean {
    return !!this.url && !!this.user && !!this.pass;
  }

  private get authorizationHeader(): string {
    const creds = this.user + ':' + this.pass;
    return 'Basic ' + btoa(creds);
  }

  private sendLog(level: LogLevel, { service, message, details }: LogParams): void {
    if (!this.isConfigured) {
      console.error('ElasticLoggerService: missing Elastic configuration');
      return;
    }

    const payload: Record<string, any> = {
      '@timestamp': new Date().toISOString(),
      level,
      service,
      Application: APPLICATION,
      Environment: this.environmentName,
      Version: this.appVersion,
      message,
    };

    if (details !== undefined) {
      payload.details = details;
    }

    const indexName = `logs-${this.environmentName.toLowerCase()}-hub/_doc`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: this.authorizationHeader,
    });

    this.httpWithoutInterceptors.post(`${this.url}/${indexName}`, payload, { headers }).subscribe({
      error: (err) => console.error('Elastic logging failed:', err)
    });
  }

  error(params: LogParams): void {
    this.sendLog(LogLevel.Error, params);
  }

  info(params: LogParams): void {
    this.sendLog(LogLevel.Information, params);
  }

  warning(params: LogParams): void {
    this.sendLog(LogLevel.Warning, params);
  }

  debug(params: LogParams): void {
    if (!this.debugMode) {
      return;
    }

    this.sendLog(LogLevel.Debug, params);
  }
}
